/**
 * CreateBusiness.jsx (iter152) — Superadmin manual tenant creation.
 *
 * Lets a platform superadmin spin up a new company + owner user without
 * the public signup / Stripe flow. Use cases:
 *   • Onboarding a customer who paid by wire / cheque
 *   • Comping a partner, beta tester, or internal staging tenant
 *   • Pre-creating an Enterprise tenant before sales closes
 *
 * Backend: POST /api/admin/companies/create (require_superadmin).
 * The response carries a one-time temp password AND a password-reset link
 * (Resend-emailed too). We surface both so the admin can hand-deliver if
 * needed.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Buildings, EnvelopeSimple, Copy, CheckCircle, Warning, ArrowClockwise,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const PLAN_OPTIONS = [
  { key: 'starter', label: 'Starter · A$99/mo' },
  { key: 'grow', label: 'Grow · A$79/mo' },
  { key: 'core', label: 'Core · A$229/mo' },
  { key: 'scale', label: 'Scale · A$599/mo' },
  { key: 'business', label: 'Business · A$1,499/mo' },
  { key: 'enterprise', label: 'Enterprise · Custom pricing' },
];

const BLANK = {
  company_name: '',
  country: 'AU',
  plan_key: 'business',
  billing_cycle: 'monthly',
  owner_name: '',
  owner_email: '',
  notes: '',
  skip_payment: true,
};

export default function CreateBusiness() {
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  const upd = (k) => (e) => {
    const v = e?.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async () => {
    setErr('');
    if (!form.company_name.trim()) return setErr('Company name is required');
    if (!form.owner_name.trim()) return setErr('Owner name is required');
    if (!form.owner_email.includes('@')) return setErr('Valid owner email required');
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/companies/create`, form, {
        withCredentials: true,
      });
      setResult(data);
      toast.success(`Business "${data.company.name}" created`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === 'string' ? detail : (e.message || 'Create failed'));
    } finally {
      setBusy(false);
    }
  };

  const copy = (val, label) => {
    navigator.clipboard.writeText(val);
    toast.success(`${label} copied`);
  };

  const reset = () => {
    setResult(null);
    setForm(BLANK);
    setErr('');
  };

  return (
    <Layout>
      <div className="p-6 max-w-3xl mx-auto" data-testid="create-business-page">
        <div className="flex items-center gap-3 mb-1">
          <Buildings size={28} weight="duotone" className="text-brand" />
          <h1 className="text-2xl font-bold tracking-tight">Create Business Account</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          Superadmin-only. Manually creates a new tenant + owner user. Use it for
          comped partners, wire-paid customers, or pre-creating an enterprise
          contract.
        </p>

        {!result ? (
          <div className="space-y-5 border border-border bg-card p-5">
            {err && (
              <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 flex items-start gap-2" data-testid="create-business-error">
                <Warning size={16} className="shrink-0 mt-0.5" />
                <span>{err}</span>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest font-bold">Company name <span className="text-rose-600">*</span></Label>
                <Input
                  data-testid="cb-company-name"
                  value={form.company_name}
                  onChange={upd('company_name')}
                  placeholder="Acme Logistics Pty Ltd"
                  autoFocus
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest font-bold">Country</Label>
                <Input
                  data-testid="cb-country"
                  value={form.country}
                  onChange={upd('country')}
                  placeholder="AU"
                  maxLength={4}
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest font-bold">Plan tier <span className="text-rose-600">*</span></Label>
                <Select value={form.plan_key} onValueChange={(v) => setForm((f) => ({ ...f, plan_key: v }))}>
                  <SelectTrigger data-testid="cb-plan-key"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLAN_OPTIONS.map((p) => (
                      <SelectItem key={p.key} value={p.key} data-testid={`cb-plan-${p.key}`}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest font-bold">Owner name <span className="text-rose-600">*</span></Label>
                <Input
                  data-testid="cb-owner-name"
                  value={form.owner_name}
                  onChange={upd('owner_name')}
                  placeholder="Alice Wonderland"
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest font-bold">Owner email <span className="text-rose-600">*</span></Label>
                <Input
                  data-testid="cb-owner-email"
                  type="email"
                  value={form.owner_email}
                  onChange={upd('owner_email')}
                  placeholder="alice@acme.com.au"
                />
              </div>

              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest font-bold">Internal notes</Label>
                <Textarea
                  data-testid="cb-notes"
                  rows={2}
                  value={form.notes}
                  onChange={upd('notes')}
                  placeholder="Why is this account being created manually? (Audit trail)"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="flex items-start gap-2 cursor-pointer" data-testid="cb-skip-payment-wrap">
                  <input
                    type="checkbox"
                    checked={form.skip_payment}
                    onChange={upd('skip_payment')}
                    data-testid="cb-skip-payment"
                    className="mt-1"
                  />
                  <span className="text-sm">
                    <span className="font-bold">Skip payment</span> — activate the account immediately, no Stripe charge.
                    {!form.skip_payment && (
                      <span className="block text-xs text-muted-foreground mt-1">
                        A Stripe Checkout URL will be returned in the response. Forward it to the customer to collect payment.
                        (Enterprise tier skips Stripe regardless — it&apos;s custom-priced.)
                      </span>
                    )}
                  </span>
                </label>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                data-testid="cb-submit"
                onClick={submit}
                disabled={busy}
                className="bg-brand hover:bg-brand/90 text-white font-bold"
              >
                {busy ? 'Creating…' : 'Create business account'}
              </Button>
            </div>
          </div>
        ) : (
          <ResultPanel result={result} onReset={reset} onCopy={copy} />
        )}
      </div>
    </Layout>
  );
}

function ResultPanel({ result, onReset, onCopy }) {
  const { company, owner, checkout_url } = result;
  return (
    <div className="space-y-4" data-testid="cb-result-panel">
      <div className="border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-start gap-3">
        <CheckCircle size={22} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-bold text-emerald-900">Business account created</div>
          <div className="text-sm text-emerald-800">
            <code className="font-mono">{company.name}</code> · plan <code className="font-mono uppercase">{company.plan_key}</code> · status <code className="font-mono">{company.status}</code>
          </div>
        </div>
      </div>

      <div className="border border-border bg-card p-5 space-y-4">
        <h2 className="text-sm uppercase tracking-widest font-bold text-muted-foreground">Owner credentials</h2>

        <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr_auto] items-center gap-2">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Email</div>
          <code className="font-mono text-sm break-all" data-testid="cb-result-email">{owner.email}</code>
          <Button size="sm" variant="ghost" onClick={() => onCopy(owner.email, 'Email')} data-testid="cb-copy-email"><Copy size={14} /></Button>

          <div className="text-xs uppercase tracking-widest text-muted-foreground">Temp password</div>
          <code className="font-mono text-sm bg-amber-50 border border-amber-200 px-2 py-1" data-testid="cb-result-password">{owner.temp_password}</code>
          <Button size="sm" variant="ghost" onClick={() => onCopy(owner.temp_password, 'Temp password')} data-testid="cb-copy-password"><Copy size={14} /></Button>

          {owner.reset_link && (
            <>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Reset link</div>
              <code className="font-mono text-xs break-all bg-muted px-2 py-1" data-testid="cb-result-reset-link">{owner.reset_link}</code>
              <Button size="sm" variant="ghost" onClick={() => onCopy(owner.reset_link, 'Reset link')} data-testid="cb-copy-reset-link"><Copy size={14} /></Button>
            </>
          )}
        </div>

        <div className="text-xs flex items-center gap-2 pt-2 border-t border-border" data-testid="cb-email-status">
          {owner.reset_email_sent ? (
            <>
              <EnvelopeSimple size={14} weight="fill" className="text-emerald-600" />
              <span className="text-emerald-800">A password-set-up email was sent to <code className="font-mono">{owner.email}</code>. Link expires in 60 minutes.</span>
            </>
          ) : (
            <>
              <Warning size={14} className="text-amber-600" />
              <span className="text-amber-800">Email failed to send (Resend not configured or rejected). Share the temp password above directly.</span>
            </>
          )}
        </div>
      </div>

      {checkout_url && (
        <div className="border border-border bg-card p-5 space-y-2">
          <h2 className="text-sm uppercase tracking-widest font-bold text-muted-foreground">Stripe checkout</h2>
          <div className="text-xs text-muted-foreground">Forward this URL to the customer to collect payment. The account stays in <code className="font-mono">pending_payment</code> until they pay.</div>
          <div className="flex items-center gap-2">
            <code className="font-mono text-xs break-all bg-muted px-2 py-1 flex-1" data-testid="cb-result-checkout-url">{checkout_url}</code>
            <Button size="sm" variant="ghost" onClick={() => onCopy(checkout_url, 'Checkout URL')} data-testid="cb-copy-checkout-url"><Copy size={14} /></Button>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <Button onClick={onReset} variant="outline" data-testid="cb-create-another">
          <ArrowClockwise size={14} className="mr-1" /> Create another
        </Button>
      </div>
    </div>
  );
}
